import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useLanguageStore, isRTL } from "@/stores/languageStore";
import { CookieConsent } from "./components/CookieConsent";
import { ScrollToTop } from "./components/ScrollToTop";



// Critical pages - load immediately
import Index from "./pages/Index";

// Lazy load Products page as well for better code splitting
const Products = lazy(() => import("./pages/Products"));

// Lazy load less critical pages for faster initial load
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Categories = lazy(() => import("./pages/Categories"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CheckoutRedirect = lazy(() => import("./pages/CheckoutRedirect"));
const NotFound = lazy(() => import("./pages/NotFound"));

const WhyBuyFromUs = lazy(() => import("./pages/WhyBuyFromUs"));
const WoodTableLanding = lazy(() => import("./pages/WoodTableLanding"));
const CarpentryLanding = lazy(() => import("./pages/CarpentryLanding"));

// Configure React Query for optimal caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const AppContent = () => {
  const { language } = useLanguageStore();

  // Update document direction based on language
  useEffect(() => {
    document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  return (
    <BrowserRouter>
      {/* Global overflow guard: prevent any page from creating horizontal scroll/cutoff */}
      <div className="min-h-screen overflow-x-hidden">
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/products" element={<Products />} />
            <Route path="/product/:handle" element={<ProductDetail />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/checkout" element={<CheckoutRedirect />} />
            
            <Route path="/lp/wood-table" element={<WoodTableLanding />} />
            <Route path="/lp/nagarut" element={<CarpentryLanding />} />
            <Route path="/why-buy-from-us" element={<WhyBuyFromUs />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        
        <CookieConsent />
      </div>
    </BrowserRouter>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppContent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
